import { Grid } from "@material-ui/core";
import { App, Events } from "app-interfaces";
import { DateTime } from "luxon";
import React from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { useStateValue } from "../../config/AppContext";
import { toDateOnly } from "../../date-utils";
import EventsClass from "../../models/Events";
import { Action, EventProps } from "../../types/StaticTypes";
import { createRenderingInfoPrinter } from "../../utils/debugging-utils";
import { FormatOptions, formatDate, isDateTime } from "../../utils/time-utils";
import WeekCalendar from "../Dashboard/WeekCalendar";
import ScheduleAvailability from "./ScheduleAvailability";
import { ScheduleCalendar } from "./ScheduleCalendar";

export interface ScheduleState {
  events: EventProps[];
  agenda: Events.IAgenda[];
  event?: EventProps;
  availability: { start: string; end: string };
}

type DashboardResponse = {
  events: Events.IAgenda[];
  start: string;
  end: string;
  event_name: string;
};
type MapSchedule = (el: Events.ISchedule) => EventProps;

const printRenderingInfo = createRenderingInfoPrinter("SchedulesListings");

/**
 * Callback function to find event with matching start date and payload start date
 * @param payload due to the untyped nature of the ninestack reducer we do not know the type of the payload and have to check
 * @param event
 */
export function matchDate(
  payload: { start?: string | Date | DateTime; event?: Events.IAgenda },
  event: Events.IAgenda
): boolean {
  if (payload.event)
    return toDateOnly(event.ev_start_time) === toDateOnly(payload.event.ev_start_time);
  if (!payload.start) return false;

  let payloadStartDate;
  if (typeof payload.start === "string") {
    payloadStartDate = toDateOnly(payload.start);
  } else if (isDateTime(payload.start)) {
    payloadStartDate = toDateOnly(payload.start.toISODate());
  } else if (payload.start instanceof Date) {
    payloadStartDate = toDateOnly(payload.start.toISOString());
  } else {
    console.error(
      "Payload start time not an instance of Date, DateTime or string!",
      typeof payload.start,
      payload.start
    );

    return false;
  }

  const eventStartTime = toDateOnly(event.ev_start_time);
  return eventStartTime === payloadStartDate;
}

export default function SchedulesListings(_props: any) {
  const {
    events: { schedule, agenda },
    Methods: {
      postEvents,
      inviteClientsToEvent,
      putEvents,
      delEvents,
      getDashboardEvents,
      getScheduledEvents,
      updateScheduledWorkout,
      shareSession,
    },
  } = useStateValue() as { events: EventsClass; Methods: App.Methods };

  const [loading, setLoading] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const mapSchedule: MapSchedule = (el) => ({
    title: el.name,
    start: el.start,
    end: el.end,
    allDay: el.is_blocked,
    resource: {
      available: el.is_available,
      full: el.is_full,
      timezone: el.timezone,
      created: el.created,
    },
  });

  const refreshSchedule = async (state: any, payload: any, newPayload: any) => {
    const scheduledEventsCall = getScheduledEvents({
      date: formatDate(new Date(newPayload.start), FormatOptions.YY_MM),
    });
    const dashboardEventsCall = getDashboardEvents({
      date: formatDate(new Date(newPayload.start), FormatOptions.ISO_DATE_ONLY),
    });
    const newSchedule = await scheduledEventsCall;
    const newDashboard = await dashboardEventsCall;
    if (!newSchedule || !newDashboard) return dispatch({ type: "refresh", payload: state });
    const { events, start, end, event_name } = newDashboard;
    if (event_name) {
      Object.assign(state, {
        event: {
          allDay: true,
          title: event_name,
        },
        events: newSchedule.map(mapSchedule),
        agenda: events,
        availability: { start, end },
      });
    } else {
      Object.assign(state, {
        event: events.find((ev: any) => matchDate(payload, ev)),
        events: newSchedule.map(mapSchedule),
        agenda: events,
        availability: { start, end },
      });
    }
    if (payload.callback) payload.callback();
    return dispatch({ type: "refresh", payload: state });
  };

  const refreshDashboard = (state: any, props: DashboardResponse) => {
    if (!props) return state;
    const { events, start, end, event_name } = props;
    if (event_name) {
      Object.assign(state, {
        event: {
          allDay: true,
          title: event_name,
        },
        agenda: events,
        availability: { start, end },
      });
    } else {
      Object.assign(state, {
        event: events[0],
        agenda: events,
        availability: { start, end },
      });
    }
    return dispatch({ type: "refresh", payload: state });
  };

  // Initial schedule value
  const initEvents: EventProps[] = schedule.map(mapSchedule);
  // State actions
  const ScheduleReducer: React.Reducer<ScheduleState, Action> = (state, { type, payload }) => {
    switch (type) {
      case "refresh":
        if (payload && loading) {
          state = payload;
          setLoading(false);
        }
        break;
      case "schedule":
        if (payload && !loading) {
          setLoading(true);
          // noinspection JSIgnoredPromiseFromCall
          refreshSchedule(state, payload, payload);
        }
        break;
      case "dashboard":
        if (loading) return state;
        if (payload && !loading) {
          setLoading(true);
          getDashboardEvents({
            date: formatDate(new Date(payload.start), 3),
          }).then((dEv) => refreshDashboard(state, dEv));
        }
        break;
      case "update-availability":
        if (payload && state.event && !loading) {
          setLoading(true);
          payload = {
            ...payload,
            id: null,
            timezone: state.event.resource ? state.event.resource.timezone : null,
            session: null,
            created: formatDate(new Date(), FormatOptions.ISO_DATETIME_WITH_Z),
          };
          postEvents(payload).then(() => refreshSchedule(state, payload, payload));
        }
        break;
      case "update-lunch":
        if (payload && payload.event && !loading) {
          setLoading(true);
          payload = {
            id: null,
            start: payload.event.ev_start_time.split(" ")[0] + "T" + payload.start,
            end: payload.event.ev_end_time.split(" ")[0] + "T" + payload.end,
            type: payload.type,
            name: payload.event.name,
            timezone: payload.event.timezone ? payload.event.timezone : null,
            session: null,
            created: formatDate(new Date(), 6),
          };
          postEvents(payload).then(() => refreshSchedule(state, payload, payload));
        }
        break;
      case "update-blocked":
        if (payload && !loading) {
          setLoading(true);
          payload = {
            id: payload.id,
            type: payload.type,
            start: payload.from,
            end: payload.to,
            name: payload.name,
            timezone: payload.timezone,
            session: null,
            created: formatDate(new Date(), 6),
          };
          putEvents(payload, payload.id).then(() => refreshSchedule(state, payload, payload));
        }
        break;
      case "update-workout":
        if (payload && payload.params && !loading) {
          setLoading(true);
          updateScheduledWorkout(payload.params).then(() =>
            refreshSchedule(state, payload, payload.params)
          );
          break;
        }
        if (payload && !loading) {
          setLoading(true);
          updateScheduledWorkout(payload).then(() => refreshSchedule(state, payload, payload));
        }
        break;
      case "update-session":
        if (payload && payload.params && !loading) {
          setLoading(true);
          const newPayload = {
            ...payload.params,
            created: formatDate(new Date(), 6),
          };
          putEvents(newPayload, newPayload.id).then(() =>
            refreshSchedule(state, payload, {
              ...newPayload,
              start: newPayload.ev_start_time ? newPayload.ev_start_time : newPayload.start,
            })
          );
          break;
        }
        if (payload && !loading) {
          setLoading(true);
          payload = {
            ...payload,
            created: formatDate(new Date(), 6),
          };
          putEvents(payload, payload.id).then(() => refreshSchedule(state, payload, payload));
        }
        break;
      case "complete-session":
        if (payload && payload.event && !loading) {
          setLoading(true);

          const newPayload = {
            ...payload.event,
            session: {
              id: payload.event.session.id,
              customer_id: payload.event.session.customer.map((el: any) => el.id),
              workout_id: payload.event.session.workout ? payload.event.session.workout.id : null,
              notes: payload.event.session.notes,
              state: "COMPLETED",
              state_reason: payload.event.session.state_reason,
              state_notes: payload.event.session.state_notes,
              state_time: payload.event.session.state_time
                ? payload.event.session.state_time
                : formatDate(new Date(), 10),
              is_booked: payload.event.session.is_booked,
            },
            created: formatDate(new Date(), 6),
          };
          putEvents(newPayload, newPayload.id).then(() =>
            refreshSchedule(state, payload, newPayload)
          );
        }
        break;
      case "share-session":
        if (payload && payload.params && payload.event && !loading) {
          setLoading(true);
          shareSession(payload.params).then(() =>
            refreshSchedule(state, payload, { start: payload.event.ev_start_time })
          );
        }
        break;
      case "cancel-session":
        if (payload && payload.event && !loading) {
          setLoading(true);
          const newPayload = {
            ...payload.event,
            session: {
              id: payload.event.session.id,
              customer_id: payload.event.session.customer.map((el: any) => el.id),
              workout_id: payload.event.session.workout ? payload.event.session.workout.id : null,
              notes: payload.event.session.notes,
              state: "CANCELLED",
              state_reason: payload.event.session.state_reason,
              state_notes: payload.event.session.state_notes,
              state_time: formatDate(new Date(), 10),
              is_booked: payload.event.session.is_booked,
            },
            created: formatDate(new Date(), 6),
          };
          putEvents(newPayload, newPayload.id).then(() =>
            refreshSchedule(state, payload, newPayload)
          );
        }
        break;
      case "addnew":
        if (payload && !loading) {
          setLoading(true);
          const invite = payload["email-invite"];
          payload = {
            id: null,
            start: payload.from,
            end: payload.to,
            type: payload.type,
            name: payload.name,
            timezone: payload.timezone,
            session:
              payload.type === "SESSION"
                ? {
                    id: uuid(),
                    customer_id: payload.customer_id,
                    state: "NONE",
                    is_booked: false,
                  }
                : null,
            created: formatDate(new Date(), 6),
          };

          postEvents(payload).then((id) => {
            if (payload.setAddNew && id === undefined) {
              payload.setAddNew(true);
            }
            return invite
              ? inviteClientsToEvent(id).then(() => refreshSchedule(state, payload, payload))
              : refreshSchedule(state, payload, payload);
          });
        }
        break;
      case "remove-event":
        if (payload && !loading) {
          setLoading(true);
          delEvents(payload.id).then(() =>
            refreshSchedule(state, payload, { start: payload.from })
          );
        }
        break;
      default:
        throw new Error(t("Invalid action type"));
    }

    return state;
  };

  // State reducer
  const [state, dispatch] = React.useReducer(ScheduleReducer, {
    events: initEvents,
    agenda: agenda,
    availability: { start: "08:00Z", end: "08:00Z" },
  });

  // Refresh Schedule effect
  React.useEffect(() => {
    dispatch({
      type: "dashboard",
      payload: { start: new Date() },
    });
  }, [dispatch]);

  printRenderingInfo();

  return (
    <div id="Schedules">
      <Grid container spacing={3}>
        <Switch>
          <Route exact path="/schedules">
            <Grid item xs={12} md={6}>
              <ScheduleCalendar events={state.events} dispatch={dispatch} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ScheduleAvailability
                agenda={state.agenda} // the list of events for that day
                availability={state.availability} // start-end
                dispatch={dispatch}
                event={state.event} // not sure what this is for
              />
            </Grid>
          </Route>
          <Route>
            <Grid item xs={12} md={12}>
              <WeekCalendar
                onChange={(date) => {
                  dispatch({ type: "dashboard", payload: { start: date } });
                }}
                isReadOnly={loading}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <ScheduleAvailability {...state} dispatch={dispatch} />
            </Grid>
          </Route>
        </Switch>
      </Grid>
      {/*<LoadingModal show={loading} />*/}
    </div>
  );
}
